// ---------------------------------------------------------------------------
// contact.content.ts — Translated content for ContactPage (all 8 locales)
// ---------------------------------------------------------------------------
import type { ContactContent, ContactContentMap } from './contact.types';

const en: ContactContent = {
  header: { title: 'Contact Us', description: 'Have a question or need assistance? We are here to help.' },
  form: {
    name: 'Full Name', namePlaceholder: 'John Doe',
    email: 'Email Address', emailPlaceholder: 'you@company.com',
    subject: 'Subject', subjectPlaceholder: 'Select a subject',
    message: 'Message', messagePlaceholder: 'Describe your inquiry...',
    submit: 'Send Message', sending: 'Sending...',
  },
  subjects: ['General Inquiry', 'Sales & Pricing', 'Technical Support', 'Partnership Opportunity', 'Billing & Payments', 'Feature Request'],
  success: { title: 'Message Sent', message: 'Thank you for reaching out. Our team will respond within 24 business hours.', sendAnother: 'Send another message' },
  officesTitle: 'Regional Offices',
  contactInfo: [
    { label: 'Global Headquarters', value: '4th Floor, Tower B, Cyber Gateway, Madhapur, Hyderabad, Telangana 500081, India' },
    { label: 'Phone', value: '+1 (415) 555-0120 (US) · +44 20 7946 0958 (UK) · +91 40 7107 0000 (India)' },
    { label: 'Email', value: 'support@hotelstack.io' },
    { label: 'Support Hours', value: '24/7 Global Support · Regional offices: Americas (EST), Europe (CET), Asia-Pacific (IST/SGT)' },
  ],
  offices: [
    { region: 'Americas', location: 'San Francisco, CA, USA', hours: '8AM - 8PM EST', languages: 'English, Spanish' },
    { region: 'Europe', location: 'London, United Kingdom', hours: '8AM - 8PM CET', languages: 'English, French, German' },
    { region: 'Middle East', location: 'Dubai, UAE', hours: '8AM - 8PM GST', languages: 'English, Arabic' },
    { region: 'Asia-Pacific', location: 'Singapore', hours: '8AM - 8PM SGT', languages: 'English, Thai, Japanese' },
  ],
  recaptcha: 'Protected by reCAPTCHA',
  recaptchaPrivacy: 'Privacy',
  recaptchaTerms: 'Terms',
};

const ar: ContactContent = {
  header: { title: 'اتصل بنا', description: 'هل لديك سؤال أو تحتاج مساعدة؟ نحن هنا للمساعدة.' },
  form: {
    name: 'الاسم الكامل', namePlaceholder: 'محمد أحمد',
    email: 'البريد الإلكتروني', emailPlaceholder: 'you@company.com',
    subject: 'الموضوع', subjectPlaceholder: 'اختر موضوعًا',
    message: 'الرسالة', messagePlaceholder: 'صف استفسارك...',
    submit: 'إرسال الرسالة', sending: 'جارٍ الإرسال...',
  },
  subjects: ['استفسار عام', 'المبيعات والتسعير', 'الدعم الفني', 'فرصة شراكة', 'الفواتير والمدفوعات', 'طلب ميزة'],
  success: { title: 'تم إرسال الرسالة', message: 'شكراً لتواصلك. سيرد فريقنا خلال ٢٤ ساعة عمل.', sendAnother: 'إرسال رسالة أخرى' },
  officesTitle: 'المكاتب الإقليمية',
  contactInfo: [
    { label: 'المقر الرئيسي العالمي', value: 'الطابق الرابع، البرج ب، سايبر غيتواي، مادابور، حيدر أباد، تيلانغانا ٥٠٠٠٨١، الهند' },
    { label: 'الهاتف', value: '+١ (٤١٥) ٥٥٥-٠١٢٠ (الولايات المتحدة) · +٤٤ ٢٠ ٧٩٤٦ ٠٩٥٨ (المملكة المتحدة) · +٩١ ٤٠ ٧١٠٧ ٠٠٠٠ (الهند)' },
    { label: 'البريد الإلكتروني', value: 'support@hotelstack.io' },
    { label: 'ساعات الدعم', value: 'دعم عالمي على مدار الساعة · المكاتب الإقليمية: الأمريكتان، أوروبا، آسيا والمحيط الهادئ' },
  ],
  offices: [
    { region: 'الأمريكتان', location: 'سان فرانسيسكو، كاليفورنيا، الولايات المتحدة', hours: '٨ ص - ٨ م بتوقيت الشرق', languages: 'الإنجليزية، الإسبانية' },
    { region: 'أوروبا', location: 'لندن، المملكة المتحدة', hours: '٨ ص - ٨ م بتوقيت وسط أوروبا', languages: 'الإنجليزية، الفرنسية، الألمانية' },
    { region: 'الشرق الأوسط', location: 'دبي، الإمارات العربية المتحدة', hours: '٨ ص - ٨ م بتوقيت الخليج', languages: 'الإنجليزية، العربية' },
    { region: 'آسيا والمحيط الهادئ', location: 'سنغافورة', hours: '٨ ص - ٨ م بتوقيت سنغافورة', languages: 'الإنجليزية، التايلاندية، اليابانية' },
  ],
  recaptcha: 'محمي بواسطة reCAPTCHA',
  recaptchaPrivacy: 'الخصوصية',
  recaptchaTerms: 'الشروط',
};

const es: ContactContent = {
  header: { title: 'Contactenos', description: 'Tiene una pregunta o necesita ayuda? Estamos aqui para ayudar.' },
  form: {
    name: 'Nombre Completo', namePlaceholder: 'Juan Garcia',
    email: 'Correo Electronico', emailPlaceholder: 'tu@empresa.com',
    subject: 'Asunto', subjectPlaceholder: 'Seleccione un asunto',
    message: 'Mensaje', messagePlaceholder: 'Describa su consulta...',
    submit: 'Enviar Mensaje', sending: 'Enviando...',
  },
  subjects: ['Consulta General', 'Ventas y Precios', 'Soporte Tecnico', 'Oportunidad de Asociacion', 'Facturacion y Pagos', 'Solicitud de Funcion'],
  success: { title: 'Mensaje Enviado', message: 'Gracias por contactarnos. Nuestro equipo respondera dentro de 24 horas habiles.', sendAnother: 'Enviar otro mensaje' },
  officesTitle: 'Oficinas Regionales',
  contactInfo: [
    { label: 'Sede Global', value: 'Piso 4, Torre B, Cyber Gateway, Madhapur, Hyderabad, Telangana 500081, India' },
    { label: 'Telefono', value: '+1 (415) 555-0120 (EE.UU.) · +44 20 7946 0958 (Reino Unido) · +91 40 7107 0000 (India)' },
    { label: 'Correo Electronico', value: 'support@hotelstack.io' },
    { label: 'Horario de Soporte', value: 'Soporte global las 24 horas · Oficinas regionales: Americas (EST), Europa (CET), Asia-Pacifico (IST/SGT)' },
  ],
  offices: [
    { region: 'Americas', location: 'San Francisco, CA, EE.UU.', hours: '8:00 - 20:00 EST', languages: 'Ingles, Espanol' },
    { region: 'Europa', location: 'Londres, Reino Unido', hours: '8:00 - 20:00 CET', languages: 'Ingles, Frances, Aleman' },
    { region: 'Medio Oriente', location: 'Dubai, EAU', hours: '8:00 - 20:00 GST', languages: 'Ingles, Arabe' },
    { region: 'Asia-Pacifico', location: 'Singapur', hours: '8:00 - 20:00 SGT', languages: 'Ingles, Tailandes, Japones' },
  ],
  recaptcha: 'Protegido por reCAPTCHA',
  recaptchaPrivacy: 'Privacidad',
  recaptchaTerms: 'Términos',
};

const fr: ContactContent = {
  header: { title: 'Contactez-nous', description: 'Une question ou besoin d\'aide? Nous sommes la pour vous aider.' },
  form: {
    name: 'Nom Complet', namePlaceholder: 'Jean Dupont',
    email: 'Adresse Email', emailPlaceholder: 'vous@entreprise.com',
    subject: 'Sujet', subjectPlaceholder: 'Selectionnez un sujet',
    message: 'Message', messagePlaceholder: 'Decrivez votre demande...',
    submit: 'Envoyer le Message', sending: 'Envoi en cours...',
  },
  subjects: ['Demande Generale', 'Ventes et Prix', 'Support Technique', 'Opportunite de Partenariat', 'Facturation et Paiements', 'Demande de Fonctionnalite'],
  success: { title: 'Message Envoye', message: 'Merci de nous avoir contactes. Notre equipe repondra dans les 24 heures ouvrables.', sendAnother: 'Envoyer un autre message' },
  officesTitle: 'Bureaux Regionaux',
  contactInfo: [
    { label: 'Siege Mondial', value: '4e etage, Tour B, Cyber Gateway, Madhapur, Hyderabad, Telangana 500081, Inde' },
    { label: 'Telephone', value: '+1 (415) 555-0120 (Etats-Unis) · +44 20 7946 0958 (Royaume-Uni) · +91 40 7107 0000 (Inde)' },
    { label: 'Email', value: 'support@hotelstack.io' },
    { label: 'Heures de Support', value: 'Support mondial 24h/24, 7j/7 · Bureaux regionaux: Ameriques (EST), Europe (CET), Asie-Pacifique (IST/SGT)' },
  ],
  offices: [
    { region: 'Ameriques', location: 'San Francisco, CA, Etats-Unis', hours: '8h - 20h EST', languages: 'Anglais, Espagnol' },
    { region: 'Europe', location: 'Londres, Royaume-Uni', hours: '8h - 20h CET', languages: 'Anglais, Francais, Allemand' },
    { region: 'Moyen-Orient', location: 'Dubai, EAU', hours: '8h - 20h GST', languages: 'Anglais, Arabe' },
    { region: 'Asie-Pacifique', location: 'Singapour', hours: '8h - 20h SGT', languages: 'Anglais, Thai, Japonais' },
  ],
  recaptcha: 'Protege par reCAPTCHA',
  recaptchaPrivacy: 'Confidentialité',
  recaptchaTerms: 'Conditions',
};

const de: ContactContent = {
  header: { title: 'Kontaktieren Sie uns', description: 'Haben Sie eine Frage oder benotigen Hilfe? Wir sind fur Sie da.' },
  form: {
    name: 'Vollstandiger Name', namePlaceholder: 'Max Mustermann',
    email: 'E-Mail-Adresse', emailPlaceholder: 'sie@firma.de',
    subject: 'Betreff', subjectPlaceholder: 'Wahlen Sie einen Betreff',
    message: 'Nachricht', messagePlaceholder: 'Beschreiben Sie Ihre Anfrage...',
    submit: 'Nachricht senden', sending: 'Wird gesendet...',
  },
  subjects: ['Allgemeine Anfrage', 'Vertrieb und Preise', 'Technischer Support', 'Partnerschaft', 'Rechnungsstellung und Zahlungen', 'Funktionswunsch'],
  success: { title: 'Nachricht gesendet', message: 'Vielen Dank fur Ihre Nachricht. Unser Team antwortet innerhalb von 24 Geschaftsstunden.', sendAnother: 'Weitere Nachricht senden' },
  officesTitle: 'Regionalbiros',
  contactInfo: [
    { label: 'Globaler Hauptsitz', value: '4. Etage, Turm B, Cyber Gateway, Madhapur, Hyderabad, Telangana 500081, Indien' },
    { label: 'Telefon', value: '+1 (415) 555-0120 (USA) · +44 20 7946 0958 (Grossbritannien) · +91 40 7107 0000 (Indien)' },
    { label: 'E-Mail', value: 'support@hotelstack.io' },
    { label: 'Support-Zeiten', value: 'Globaler Support rund um die Uhr · Regionalbiros: Amerika (EST), Europa (CET), Asien-Pazifik (IST/SGT)' },
  ],
  offices: [
    { region: 'Amerika', location: 'San Francisco, CA, USA', hours: '8:00 - 20:00 EST', languages: 'Englisch, Spanisch' },
    { region: 'Europa', location: 'London, Vereinigtes Konigreich', hours: '8:00 - 20:00 CET', languages: 'Englisch, Franzosisch, Deutsch' },
    { region: 'Naher Osten', location: 'Dubai, VAE', hours: '8:00 - 20:00 GST', languages: 'Englisch, Arabisch' },
    { region: 'Asien-Pazifik', location: 'Singapur', hours: '8:00 - 20:00 SGT', languages: 'Englisch, Thai, Japanisch' },
  ],
  recaptcha: 'Geschutzt durch reCAPTCHA',
  recaptchaPrivacy: 'Datenschutz',
  recaptchaTerms: 'Nutzungsbedingungen',
};

const th: ContactContent = {
  header: { title: 'ติดต่อเรา', description: 'มีคำถามหรือต้องการความช่วยเหลือ? เราพร้อมช่วยเหลือคุณ' },
  form: {
    name: 'ชื่อ-นามสกุล', namePlaceholder: 'สมชาย ใจดี',
    email: 'อีเมล', emailPlaceholder: 'you@company.com',
    subject: 'หัวข้อ', subjectPlaceholder: 'เลือกหัวข้อ',
    message: 'ข้อความ', messagePlaceholder: 'อธิบายคำถามของคุณ...',
    submit: 'ส่งข้อความ', sending: 'กำลังส่ง...',
  },
  subjects: ['สอบถามทั่วไป', 'การขายและราคา', 'สนับสนุนทางเทคนิค', 'โอกาสพันธมิตร', 'การเรียกเก็บเงินและการชำระ', 'ขอฟีเจอร์'],
  success: { title: 'ส่งข้อความแล้ว', message: 'ขอบคุณที่ติดต่อเรา ทีมของเราจะตอบกลับภายใน 24 ชั่วโมงทำการ', sendAnother: 'ส่งข้อความอื่น' },
  officesTitle: 'สำนักงานภูมิภาค',
  contactInfo: [
    { label: 'สำนักงานใหญ่ระดับโลก', value: 'ชั้น 4, อาคาร B, ไซเบอร์ เกตเวย์, มาดาปูร์, ไฮเดอราบาด, เตลังคานา 500081, อินเดีย' },
    { label: 'โทรศัพท์', value: '+1 (415) 555-0120 (สหรัฐฯ) · +44 20 7946 0958 (สหราชอาณาจักร) · +91 40 7107 0000 (อินเดีย)' },
    { label: 'อีเมล', value: 'support@hotelstack.io' },
    { label: 'เวลาให้บริการ', value: 'สนับสนุนทั่วโลกตลอด 24 ชั่วโมง · สำนักงานภูมิภาค: อเมริกา (EST), ยุโรป (CET), เอเชียแปซิฟิก (IST/SGT)' },
  ],
  offices: [
    { region: 'อเมริกา', location: 'ซานฟรานซิสโก, แคลิฟอร์เนีย, สหรัฐอเมริกา', hours: '8AM - 8PM EST', languages: 'อังกฤษ, สเปน' },
    { region: 'ยุโรป', location: 'ลอนดอน, สหราชอาณาจักร', hours: '8AM - 8PM CET', languages: 'อังกฤษ, ฝรั่งเศส, เยอรมัน' },
    { region: 'ตะวันออกกลาง', location: 'ดูไบ, สหรัฐอาหรับเอมิเรตส์', hours: '8AM - 8PM GST', languages: 'อังกฤษ, อาหรับ' },
    { region: 'เอเชียแปซิฟิก', location: 'สิงคโปร์', hours: '8AM - 8PM SGT', languages: 'อังกฤษ, ไทย, ญี่ปุ่น' },
  ],
  recaptcha: 'ป้องกันโดย reCAPTCHA',
  recaptchaPrivacy: 'ความเป็นส่วนตัว',
  recaptchaTerms: 'ข้อกำหนด',
};

const ja: ContactContent = {
  header: { title: 'お問い合わせ', description: 'ご質問やサポートが必要ですか？お気軽にお問い合わせください。' },
  form: {
    name: '氏名', namePlaceholder: '山田太郎',
    email: 'メールアドレス', emailPlaceholder: 'you@company.com',
    subject: '件名', subjectPlaceholder: '件名を選択',
    message: 'メッセージ', messagePlaceholder: 'お問い合わせ内容をご記入ください...',
    submit: 'メッセージを送信', sending: '送信中...',
  },
  subjects: ['一般的なお問い合わせ', '営業・料金', 'テクニカルサポート', 'パートナーシップ', '請求・お支払い', '機能リクエスト'],
  success: { title: 'メッセージ送信完了', message: 'お問い合わせありがとうございます。24営業時間以内にご返信いたします。', sendAnother: '別のメッセージを送信' },
  officesTitle: '地域オフィス',
  contactInfo: [
    { label: 'グローバル本社', value: '4階、タワーB、サイバーゲートウェイ、マダプール、ハイデラバード、テランガーナ 500081、インド' },
    { label: '電話', value: '+1 (415) 555-0120（米国） · +44 20 7946 0958（英国） · +91 40 7107 0000（インド）' },
    { label: 'メール', value: 'support@hotelstack.io' },
    { label: 'サポート時間', value: '年中無休24時間グローバルサポート · 地域オフィス: 米州 (EST)、欧州 (CET)、アジア太平洋 (IST/SGT)' },
  ],
  offices: [
    { region: '米州', location: 'サンフランシスコ, カリフォルニア, アメリカ', hours: '8AM - 8PM EST', languages: '英語、スペイン語' },
    { region: '欧州', location: 'ロンドン, イギリス', hours: '8AM - 8PM CET', languages: '英語、フランス語、ドイツ語' },
    { region: '中東', location: 'ドバイ, UAE', hours: '8AM - 8PM GST', languages: '英語、アラビア語' },
    { region: 'アジア太平洋', location: 'シンガポール', hours: '8AM - 8PM SGT', languages: '英語、タイ語、日本語' },
  ],
  recaptcha: 'reCAPTCHAで保護されています',
  recaptchaPrivacy: 'プライバシー',
  recaptchaTerms: '利用規約',
};

const zh: ContactContent = {
  header: { title: '联系我们', description: '有问题或需要帮助？我们随时为您服务。' },
  form: {
    name: '姓名', namePlaceholder: '张三',
    email: '电子邮件', emailPlaceholder: 'you@company.com',
    subject: '主题', subjectPlaceholder: '选择主题',
    message: '留言', messagePlaceholder: '请描述您的问题...',
    submit: '发送消息', sending: '发送中...',
  },
  subjects: ['一般咨询', '销售与定价', '技术支持', '合作机会', '账单与付款', '功能请求'],
  success: { title: '消息已发送', message: '感谢您的联系。我们的团队将在24个工作小时内回复。', sendAnother: '发送另一条消息' },
  officesTitle: '区域办事处',
  contactInfo: [
    { label: '全球总部', value: '4楼, B座, Cyber Gateway, Madhapur, 海得拉巴, 特伦甘纳邦 500081, 印度' },
    { label: '电话', value: '+1 (415) 555-0120 (美国) · +44 20 7946 0958 (英国) · +91 40 7107 0000 (印度)' },
    { label: '电子邮件', value: 'support@hotelstack.io' },
    { label: '服务时间', value: '全天候全球支持 · 区域办事处: 美洲 (EST)、欧洲 (CET)、亚太 (IST/SGT)' },
  ],
  offices: [
    { region: '美洲', location: '旧金山, 加利福尼亚, 美国', hours: '8AM - 8PM EST', languages: '英语、西班牙语' },
    { region: '欧洲', location: '伦敦, 英国', hours: '8AM - 8PM CET', languages: '英语、法语、德语' },
    { region: '中东', location: '迪拜, 阿联酋', hours: '8AM - 8PM GST', languages: '英语、阿拉伯语' },
    { region: '亚太', location: '新加坡', hours: '8AM - 8PM SGT', languages: '英语、泰语、日语' },
  ],
  recaptcha: '受 reCAPTCHA 保护',
  recaptchaPrivacy: '隐私',
  recaptchaTerms: '条款',
};

const hi: ContactContent = {
  header: { title: 'संपर्क करें', description: 'कोई प्रश्न है या सहायता चाहिए? हम यहां मदद के लिए हैं।' },
  form: {
    name: 'पूरा नाम', namePlaceholder: 'राजेश कुमार',
    email: 'ईमेल पता', emailPlaceholder: 'you@company.com',
    subject: 'विषय', subjectPlaceholder: 'विषय चुनें',
    message: 'संदेश', messagePlaceholder: 'अपनी पूछताछ का वर्णन करें...',
    submit: 'संदेश भेजें', sending: 'भेजा जा रहा है...',
  },
  subjects: ['सामान्य पूछताछ', 'बिक्री और मूल्य', 'तकनीकी सहायता', 'साझेदारी का अवसर', 'बिलिंग और भुगतान', 'सुविधा अनुरोध'],
  success: { title: 'संदेश भेजा गया', message: 'संपर्क करने के लिए धन्यवाद। हमारी टीम 24 कार्य घंटों के भीतर जवाब देगी।', sendAnother: 'एक और संदेश भेजें' },
  officesTitle: 'क्षेत्रीय कार्यालय',
  contactInfo: [
    { label: 'वैश्विक मुख्यालय', value: 'चौथी मंजिल, टावर बी, साइबर गेटवे, मादापुर, हैदराबाद, तेलंगाना 500081, भारत' },
    { label: 'फोन', value: '+1 (415) 555-0120 (अमेरिका) · +44 20 7946 0958 (ब्रिटेन) · +91 40 7107 0000 (भारत)' },
    { label: 'ईमेल', value: 'support@hotelstack.io' },
    { label: 'सहायता समय', value: 'चौबीसों घंटे वैश्विक सहायता · क्षेत्रीय कार्यालय: अमेरिका (EST), यूरोप (CET), एशिया-प्रशांत (IST/SGT)' },
  ],
  offices: [
    { region: 'अमेरिका', location: 'सैन फ्रांसिस्को, CA, USA', hours: '8AM - 8PM EST', languages: 'अंग्रेज़ी, स्पैनिश' },
    { region: 'यूरोप', location: 'लंदन, यूनाइटेड किंगडम', hours: '8AM - 8PM CET', languages: 'अंग्रेज़ी, फ्रेंच, जर्मन' },
    { region: 'मध्य पूर्व', location: 'दुबई, UAE', hours: '8AM - 8PM GST', languages: 'अंग्रेज़ी, अरबी' },
    { region: 'एशिया-प्रशांत', location: 'सिंगापुर', hours: '8AM - 8PM SGT', languages: 'अंग्रेज़ी, थाई, जापानी' },
  ],
  recaptcha: 'reCAPTCHA द्वारा सुरक्षित',
  recaptchaPrivacy: 'गोपनीयता',
  recaptchaTerms: 'नियम',
};

export const CONTACT_CONTENT: ContactContentMap = { en, ar, es, fr, de, th, ja, zh, hi };
